import { DivisionLabel } from "@cmp-src/data-display/labels/DivisionLabel";
import { OfficeLabel } from "@cmp-src/data-display/labels/OfficeLabel";
import { Division, Office } from "@gettactic/api";
import { useDivisionsMenuQuery } from "@gettactic/hooks";
import { divisionAndOfficesToCategorySelectItems } from "@cmp-src/divisions/DivisionSelector/helpers";
import { CategorySelect } from "@cmp-src/form/CategorySelect/CategorySelect";
import {
  Category,
  CategorySelectProps
} from "@cmp-src/form/CategorySelect/types";
import { Button, ButtonProps } from "@cmp-src/primitives/button";
import { cn } from "@gettactic/helpers";
import React from "react";
import { DivisionSelectorWithApply } from "./DivisionSelectorWithApply";

export interface DivisionSelectorProps
  extends Omit<CategorySelectProps, "categories"> {
  selectedDivision?: Division | null;
  selectedOffice?: Office | null;
  onSelectDivision?: (division?: Division) => void;
  onSelectOffice?: (office?: Office) => void;
  /** Shows "All Offices and Divisions" when nothing is selected. */
  hasAllItem?: boolean;
  /** Adds a "No Office" choice. */
  hasNoOfficeOption?: boolean;
  noOfficeLabel?: string;
  triggerProps?: ButtonProps;
  /** Confirms the choice with Apply (and clears it with Clear). */
  withApply?: boolean;
  /** Picks several offices and divisions, with checkboxes. */
  isMultiselect?: boolean;
  selectedOffices?: Office[];
  selectedDivisions?: Division[];
  onSelectOffices?: (offices: Office[]) => void;
  onSelectDivisions?: (divisions: Division[]) => void;
}

/**
 * Picks an office or a division from the organization's tree, loaded from the API; offices
 * are told apart by their `offi_` id. `isMultiselect` picks several.
 */
export const DivisionSelector: React.FC<DivisionSelectorProps> = (props) => {
  const {
    hasAllItem,
    hasNoOfficeOption,
    noOfficeLabel = "No Office",
    selectedDivision,
    selectedOffice,
    onSelectDivision,
    onSelectOffice,
    triggerProps,
    withApply,
    isMultiselect,
    selectedOffices,
    selectedDivisions,
    onSelectOffices,
    onSelectDivisions,
    ...rest
  } = props;

  // Hooks run on every render, whichever version renders below
  const divisions = useDivisionsMenuQuery();
  const root = divisions.data;

  const categories = React.useMemo(() => {
    const categories: Category = {
      name: "Divisions",
      id: "divisions",
      children: []
    };
    if (root?.tree) {
      categories.children =
        divisionAndOfficesToCategorySelectItems(root.tree)?.children || [];
    }

    // Add "No Office" option at the beginning if enabled
    if (hasNoOfficeOption) {
      categories.children = [
        {
          id: "no_office",
          name: noOfficeLabel,
          keywords: [noOfficeLabel, "none", "no office"],
          data: undefined
        },
        ...(categories.children || [])
      ];
    }

    return categories;
  }, [root?.tree, hasNoOfficeOption, noOfficeLabel]);

  // If withApply or isMultiselect is enabled, use the enhanced component
  if (withApply || isMultiselect) {
    return <DivisionSelectorWithApply {...props} />;
  }

  const trigger = (
    <Button
      variant={"trigger"}
      {...triggerProps}
      className={cn("w-full min-w-0", triggerProps?.className)}
    >
      <span className={"min-w-0 flex-1 truncate text-left"}>
        {hasAllItem && !selectedOffice && !selectedDivision && (
          <span className={"truncate"}>All Offices and Divisions</span>
        )}
        {hasNoOfficeOption && !selectedOffice && !selectedDivision && (
          <span className={"truncate"}>{noOfficeLabel}</span>
        )}
        {selectedOffice && (
          <OfficeLabel
            office={selectedOffice}
            iconClassName={"text-neutral-1000"}
            className={"truncate min-w-0"}
          />
        )}
        {selectedDivision && (
          <span className={"truncate min-w-0 inline-flex"}>
            <DivisionLabel division={selectedDivision} />
          </span>
        )}
      </span>
    </Button>
  );

  return (
    <CategorySelect
      categories={categories}
      trigger={trigger}
      value={{
        name:
          selectedOffice?.name ||
          selectedDivision?.name ||
          (hasNoOfficeOption && !selectedOffice && !selectedDivision
            ? noOfficeLabel
            : ""),
        id:
          selectedOffice?.id ||
          selectedDivision?.id ||
          (hasNoOfficeOption && !selectedOffice && !selectedDivision
            ? "no_office"
            : "")
      }}
      onSelect={(category) => {
        if (category.id === "no_office") {
          onSelectOffice?.(undefined);
          onSelectDivision?.(undefined);
          return;
        }
        const isOffice = category.id.startsWith("offi_");
        if (isOffice) {
          onSelectOffice?.(category.data as Office);
        } else {
          onSelectDivision?.(category.data as Division);
        }
      }}
      {...rest}
    />
  );
};
